import { type BannerAction, type BannerItem, type BannerSeverity, selectActiveBanner } from '../../../domain'

import { Usecase } from '../Usecase'

type BannerOptions = Readonly<{
    severity: BannerSeverity
    title: string
    description?: string
    action?: BannerAction
    dismissKey: string
}>

export class BannerUsecase extends Usecase {
    /**
     * Not `AppState`: nothing renders off this directly, it's session-scoped bookkeeping local to
     * this usecase instance (which itself lives for the app session), mirroring `ToastUsecase`'s
     * `nextId` counter.
     */
    private readonly dismissedKeys = new Set<string>()

    /**
     * Shows a banner, unless its `dismissKey` was already dismissed this session and hasn't been
     * `clear`-ed since. At most one banner is ever visible - a new call replaces whatever's
     * currently active. The plan leaves "two simultaneous banner-worthy conditions" unspecified;
     * last-shown-wins is the simplest, most predictable rule absent a spec either way.
     */
    show (options: BannerOptions): void {
        if (this.dismissedKeys.has(options.dismissKey)) {
            return
        }

        const banner: BannerItem = {
            severity: options.severity,
            title: options.title,
            dismissKey: options.dismissKey,
            ...(options.description !== undefined && { description: options.description }),
            ...(options.action !== undefined && { action: options.action }),
        }

        this.setState({ activeBanner: banner })
    }

    /**
     * Called by the feature once the underlying condition resolves (e.g. the version mismatch is
     * fixed) - forgets the dismissal for `dismissKey`, so a future recurrence of the condition
     * shows the banner again ("reappears if condition persists"), and hides it now if it's the one
     * currently active (the condition it represents is gone, so it shouldn't still be shown).
     */
    clear (dismissKey: BannerItem['dismissKey']): void {
        this.dismissedKeys.delete(dismissKey)

        if (selectActiveBanner(this.getState())?.dismissKey === dismissKey) {
            this.setState({ activeBanner: null })
        }
    }

    /**
     * User-initiated dismiss (the banner's own close button) - suppresses it until `clear` is
     * called for its `dismissKey`. Not in the plan's illustrative `BannerUsecase` sketch (`show`/
     * `clear` only), but necessary: `clear` is the *feature's* signal that a condition resolved,
     * distinct from the *user* asking to stop seeing a still-active one.
     */
    dismiss (): void {
        const banner = selectActiveBanner(this.getState())

        if (!banner) {
            return
        }

        this.dismissedKeys.add(banner.dismissKey)
        this.setState({ activeBanner: null })
    }
}
